import { inflate, ungzip } from 'pako';

export const TG_WALLPAPER_PATTERN_MIME_TYPE = 'application/x-tgwallpattern';
export const WALLPAPER_RENDERER_VERSION = '2026-04-18-runtime-pattern-v1';

const SVG_MIME_TYPE = 'image/svg+xml';

export function isTgWallPatternMimeType(mimeType?: string) {
  return mimeType === TG_WALLPAPER_PATTERN_MIME_TYPE;
}

export function isDecodedPatternMimeType(mimeType?: string) {
  return mimeType === SVG_MIME_TYPE;
}

export function isPatternWallpaperMimeType(mimeType?: string) {
  return isTgWallPatternMimeType(mimeType) || isDecodedPatternMimeType(mimeType);
}

export async function decodeTgWallPatternToSvgBlob(blob: Blob): Promise<Blob | undefined> {
  try {
    const data = await readBlobAsUint8Array(blob);
    let svgText: string;
    try {
      svgText = decodeUtf8(ungzip(data));
    } catch {
      const inflated = inflate(data, { to: 'string' });
      svgText = typeof inflated === 'string' ? inflated : decodeUtf8(inflated);
    }

    if (!svgText || !svgText.trim().startsWith('<')) {
      return undefined;
    }

    return new Blob([svgText], { type: SVG_MIME_TYPE });
  } catch {
    return undefined;
  }
}

export async function getCacheableWallpaperBlob({
  blob,
  mimeType,
}: {
  blob: Blob;
  mimeType?: string;
}) {
  const resolvedMimeType = mimeType || blob.type;

  if (!isTgWallPatternMimeType(resolvedMimeType) && !isTgWallPatternMimeType(blob.type)) {
    return blob;
  }

  const decodedBlob = await decodeTgWallPatternToSvgBlob(blob);
  return decodedBlob || blob;
}

function decodeUtf8(data: Uint8Array) {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(data);
  }

  let ascii = '';
  data.forEach((byte) => {
    ascii += String.fromCharCode(byte);
  });

  try {
    return decodeURIComponent(escape(ascii));
  } catch {
    return ascii;
  }
}

async function readBlobAsUint8Array(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return new Uint8Array(await blob.arrayBuffer());
  }

  const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const { result } = event.target || {};
      if (result instanceof ArrayBuffer) {
        resolve(result);
      } else {
        reject(new Error('Failed to read blob as array buffer'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read blob'));
    reader.readAsArrayBuffer(blob);
  });

  return new Uint8Array(arrayBuffer);
}
